// authServices.ts
import axiosClient from './axios-client'; // Import the axiosClient with interceptors
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";

// Function to handle user signup


// Function to handle user login


// Function to fetch user details

interface VerifyResponse {
  statusCode: number;
  message: string;
  statusId: number;
  statusName: string;
  // Only present on the 423/TemporarilyLocked response (5 failed logins in a
  // row) — UTC ISO timestamp of when the lock lifts.
  lockedUntil?: string;
}

export const VerifyAccountApi = async (email: string): Promise<VerifyResponse> => {
  try {
    const response:VerifyResponse = await axiosClient.get("/SignUp/VerifyAccount", {
      params: { Email: email },
      validateStatus: (status) => true,
    });

    return response;
  } catch (error: any) {
    console.error("Error verifying account:", error);
    throw error; // Throwing the full error object for better error handling
  }
};

// Login flow only: same account-status check as VerifyAccountApi, but also
// enforces the 5-failed-attempts/15-minute lockout (backend's VerifyLoginEmail).
// Not for signup/forgot-password -- those must not trip or reset the lockout.
export const VerifyLoginEmailApi = async (email: string): Promise<VerifyResponse> => {
  try {
    const response: VerifyResponse = await axiosClient.get("/SignUp/VerifyLoginEmail", {
      params: { Email: email },
      validateStatus: (status) => true,
    });

    return response;
  } catch (error: any) {
    console.error("Error verifying login email:", error);
    throw error;
  }
};

// Tells the backend a Firebase email/password sign-in just failed (wrong
// password / user-not-found), since password checking happens against
// Firebase directly and never touches our API otherwise — this is the only
// way the backend's 5-attempts/15-minute lockout knows an attempt failed.
// Best-effort: a failure here shouldn't block the login flow the user is
// already seeing an error for.
export const reportFailedLogin = async (email: string): Promise<void> => {
  try {
    await axiosClient.post("/SignUp/ReportFailedLogin", { Email: email });
  } catch (error) {
    console.error("Error reporting failed login:", error);
  }
};

// Tells the backend a sign-in just succeeded, clearing the accumulated
// failed-attempt count. VerifyLoginEmailApi runs before the Firebase attempt
// (to route Inactive/Blocked/Deleted accounts) so it can't double as this
// signal -- this must only be called once sign-in is actually confirmed.
// Best-effort: a failure here shouldn't block the login flow.
export const reportLoginSuccess = async (email: string): Promise<void> => {
  try {
    await axiosClient.post("/SignUp/ReportLoginSuccess", { Email: email });
  } catch (error) {
    console.error("Error reporting login success:", error);
  }
};

export const CreateAccountApi = async () => {
  try {
    const response = await axiosClient.post("/User/CreateAccount");
    return response;
  } catch (error: any) {
    console.error("Error creating account:", error);
    throw new Error(error.response?.data?.message || "Error creating account");
  }
};

export interface UploadFileResponse {
  message: string;
  fileUrl: string;
  userId: number;
}

export const uploadFileApi = async (file: File): Promise<UploadFileResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosClient.post('/File/FileUpload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    // Access response.data (assuming interceptor returns full response object)
    const data = response.data || response; // Fallback to response if interceptor transforms it
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid upload response');
    }
    return data as UploadFileResponse;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

export interface VerifyUploadResponse {
  message: string; 
  aiResponse: {
    task_id: string;
    status: string;
  };
}

export const verifyUploadApi = async (imageUrl: string, userId: number): Promise<VerifyUploadResponse> => {
  try {
    const response = await axiosClient.post('/File/VerifyUpload', {
      imageUrl,
      userId
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    // Access response.data (assuming interceptor returns full response object)
    const data = response.data || response; // Fallback to response if interceptor transforms it
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid verify response');
    }
    return data as VerifyUploadResponse;
  } catch (error) {
    console.error('Error verifying upload:', error);
    throw error;
  }
}

export const fetchAirlinesApi = async (query: string) => {
  try {
    const response = await axiosClient.get("/User/airlines", {
      params: { search: query },
      headers: { "Content-Type": "application/json" },
    });
    
    return response; // Returns an array of airlines
  } catch (error) {
    console.error("Error fetching airlines:", error);
    throw error;
  }
};

export const fetchHomeBasesApi = async (query: string) => {
  try {
    const response = await axiosClient.get("/User/airports", {
      params: { search: query },
      headers: { "Content-Type": "application/json" },
    });
    return response; // Array of home bases
  } catch (error) {
    console.error("Error fetching home bases:", error);
    throw error;
  }
};

export const updateAccountApi = async (
  firstName: string,
  lastName: string,
  employerAirlineId: number,
  homebaseId: number,
  referralCode: string
): Promise<void> => {
  try {
    await axiosClient.post('/User/UpdateAccount', {
      firstName,
      lastName,
      employerAirlineId,
      homebaseId,
      referralCode,
    });
  } catch (error) {
    console.error('Error updating account:', error);
    throw error;
  }
};

export const validateReferralCodeApi = async (referralCode: string): Promise<void> => {
  try {
    await axiosClient.get('/Referrals/ValidateReferralCode', {
      params: { referralCode },
    });
  } catch (error) {
    console.error('Error validating referral code:', error);
    throw error;
  }
};

export const applyReferralApi = async (
  referralCode: string,
): Promise<{ message?: string }> => {
  try {
    const response = (await axiosClient.post('/User/ApplyReferral', {
      referralCode,
    })) as unknown as { message?: string };
    return response ?? {};
  } catch (error: any) {
    console.error('Error applying referral:', error);
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      'Failed to apply referral code.';
    throw new Error(message);
  }
};

export const checkEmailVerification = (email: string, tempPassword: string, callback: () => void) => {
  const auth = getAuth();

  // Stop polling after 5 minutes (100 attempts at 3s) so we never loop forever
  // — protects against forgotten cleanups and Firebase rate-limiting on bad creds.
  const MAX_ATTEMPTS = 100;
  let attempts = 0;

  const interval = setInterval(async () => {
    attempts += 1;
    if (attempts > MAX_ATTEMPTS) {
      clearInterval(interval);
      return;
    }

    try {
      if (!email) return;

      // Try signing in to refresh the user status
      await signInWithEmailAndPassword(auth, email, tempPassword);
      await auth.currentUser?.reload();

      if (auth.currentUser?.emailVerified) {
        clearInterval(interval); // Stop polling
        callback(); // Execute callback when email is verified
      }
    } catch (error) {
      console.error("Error checking email verification:", error);
    }
  }, 3000); // Check every 3 seconds

  return () => clearInterval(interval); // Return cleanup function
};
